// =====================================================================
// Captura da tela no instante do relato.
//
// Por que renderizar o DOM e não usar getDisplayMedia():
//   getDisplayMedia abre o diálogo do navegador ("escolha o que
//   compartilhar"). Isso (a) exige um segundo ato deliberado de quem já
//   está irritado com um erro, (b) captura a tela INTEIRA — inclusive a
//   janela do WhatsApp ao lado — e (c) some no Safari sem HTTPS. O
//   modern-screenshot serializa o próprio DOM: pega exatamente a área do
//   portal, sem prompt e sem capturar nada que não seja nosso.
//
// A contrapartida honesta: o que é desenhado fora do DOM (canvas com
// contexto WebGL, <iframe> de outra origem) sai em branco. Nada disso
// existe no portal hoje; se um mapa embutido entrar, esta é a linha que
// precisa ser revisitada.
// =====================================================================

/** Marca elementos que NÃO devem aparecer na captura (o próprio modal, o FAB). */
export const NOSHOT_ATTR = "data-bn-noshot";

/** Teto de largura da imagem. Uma tela 4K produziria ~4 MB de PNG por relato. */
const LARGURA_MAXIMA = 1600;

/** Qualidade do WebP. 0,72 mantém texto de tabela legível a ~120 KB por captura. */
const QUALIDADE = 0.72;

export interface Captura {
  /** data URL pronta para o campo `screenshot` da API. */
  dataUrl: string;
  largura: number;
  altura: number;
  bytes: number;
}

/**
 * Fotografa a página visível.
 *
 * Precisa rodar ANTES de qualquer modal aparecer — daí o botão capturar
 * primeiro e só então abrir o formulário. Capturar depois renderizaria o
 * próprio formulário de relato por cima do problema, que é o retrato mais
 * inútil possível.
 */
export async function capturarTela(): Promise<Captura | null> {
  try {
    // import() dinâmico: a biblioteca de captura (~30 KB) só desce quando
    // alguém de fato clica em "Reportar erro". Cobrá-la de todo mundo, em
    // todo carregamento, para um recurso usado algumas vezes por mês é
    // exatamente o tipo de peso que torna o portal lento na operação.
    const { domToCanvas } = await import("modern-screenshot");
    const alvo = document.body;
    const escala = Math.min(1, LARGURA_MAXIMA / Math.max(alvo.scrollWidth || window.innerWidth, 1));

    const canvas = await domToCanvas(alvo, {
      scale: escala,
      backgroundColor: "#ffffff",
      // Elementos flutuantes do próprio mecanismo de relato ficam de fora.
      filter: (node) => !(node instanceof Element && node.hasAttribute(NOSHOT_ATTR)),
      // Fontes externas travariam a captura esperando download; o portal usa
      // a pilha de fontes do sistema, então não há o que embutir.
      font: false,
      timeout: 8000,
    });

    // WebP primeiro (metade do peso do PNG com o mesmo texto legível); PNG
    // é a rede de segurança para navegadores que ignoram o tipo pedido e
    // devolvem PNG silenciosamente — o schema da API aceita os dois.
    let dataUrl = canvas.toDataURL("image/webp", QUALIDADE);
    if (!dataUrl.startsWith("data:image/webp")) dataUrl = canvas.toDataURL("image/png");

    return {
      dataUrl,
      largura: canvas.width,
      altura: canvas.height,
      bytes: Math.round((dataUrl.length - dataUrl.indexOf(",") - 1) * 0.75),
    };
  } catch (e) {
    // Uma captura que falha NÃO pode impedir o relato: o texto e a trilha
    // já valem um chamado. Registrar e seguir.
    console.warn("Não foi possível capturar a tela:", e);
    return null;
  }
}

/** Contexto técnico do navegador — vai junto no chamado. */
export function coletarAmbiente(): {
  userAgent: string; viewport: string; screen: string; language: string; timezone: string; appVersion: string;
} {
  return {
    userAgent: navigator.userAgent.slice(0, 500),
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    screen: `${window.screen?.width ?? "?"}×${window.screen?.height ?? "?"} @${window.devicePixelRatio ?? 1}x`,
    language: navigator.language ?? "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    // Injetada no build (vite define). "dev" quando roda pelo servidor local.
    appVersion: (import.meta.env?.VITE_APP_VERSION as string) ?? "dev",
  };
}
